import { forwardRef, memo, useEffect, useRef, useState } from 'react';
import type { RefAttributes } from 'react';
import { css } from '@linaria/core';

import { cellSelectedClassname } from './style';
import { getCellStyle, getCellClassname } from './utils';
import type { CellRendererProps } from './types';
import { SELECT_COLUMN_KEY } from './Columns';
import { useCombinedRefs } from './hooks/useCombinedRefs';
import { toString } from 'lodash';
import { isEqual } from 'lodash'

const cellCopied = css`
  background-color: #ccccff;
`;

const cellDraggedOver = css`
  background-color: #ccccff;

  &.${cellCopied} {
    background-color: #9999ff;
  }
`;



const cellDraggedOverClassname = `rdg-cell-dragged-over ${cellDraggedOver}`;

const cellDragHandle = css`
  cursor: move;
  position: absolute;
  right: 0;
  bottom: 0;
  width: 8px;
  height: 8px;
  background-color: var(--selection-color);

  &:hover {
    width: 16px;
    height: 16px;
    border: 2px solid var(--selection-color);
    background-color: var(--background-color);
  }
`;

const cellDropOverClassname = css`
&:before {
  content: '';
  position: absolute;
  width: calc(100%);
  height: 2px;
  background: var(--row-selected-background-color);
  top: 0px;
  z-index: 1;
}
`;

const cellIsRowSelectClassname = css`
  contain: none;
  overflow: visible;
`;

const cellModifiedClassname = css`
  &.rdg-cell{
    background-color: var(--table-edit-color);
  }
`;


const cellDragHandleClassname = `rdg-cell-drag-handle ${cellDragHandle}`;

function Cell<R, SR>({
  className,
  column,
  columnIdx,
  drop,
  drag,
  isCellSelected,
  isCellFirstSelected,
  isDraggedOver,
  isRowSelected,
  row,
  rowIdx,
  dragHandleProps,
  isRowCellSelected,
  isColumnSelected,
  setMouseDown,
  lazyStore,
  isModified,
  onClick,
  onDoubleClick,
  onContextMenu,
  onRowChange,
  onRowReorder,
  onGridSelect,
  enableRowRecord,
  selectedRange,
  ...props
}: CellRendererProps<R, SR>, ref: React.Ref<HTMLDivElement>) {
  const { cellClass } = column;
  const cellRef = useRef<HTMLDivElement>();
  const unionRef = useCombinedRefs(ref, drop[1], drag[1], cellRef);
  const normalRef = useCombinedRefs(cellRef, ref);
  const isSelectColumn = SELECT_COLUMN_KEY === column.key;
  const [innerMouseDownPosition, setInnerMouseDownPosition] = useState<{ clientX: number; clientY: number; isSelected: boolean; } | null>();
  className = getCellClassname(
    column,
    {
      [cellSelectedClassname]: isCellSelected || isColumnSelected,
      [cellDraggedOverClassname]: isDraggedOver,
      [cellDropOverClassname]: drop[0].isOver && isSelectColumn,
      [cellIsRowSelectClassname]: (isRowSelected && isSelectColumn),
      [cellModifiedClassname]: isModified
    },
    typeof cellClass === 'function' ? cellClass(row, isRowCellSelected) : cellClass,
    className
  );

  useEffect(() => {
    function _tmp() {
      if (innerMouseDownPosition) {
        setInnerMouseDownPosition(null);
      }
    }
    window.addEventListener('mouseup', _tmp);
    return () => {
      window.removeEventListener('mouseup', _tmp);
    }
  }, [innerMouseDownPosition])


  function selectCellWrapper(isShiftClick: boolean, enableEditor?: boolean) {
    onGridSelect({ 
      rowIdx,
      columnIdx,
      isShiftClick,
      enableEditor
    });
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.target as HTMLDivElement)) {
      selectCellWrapper(false, true);
      onDoubleClick?.(event);
    }
  }

  function handleRowChange(newRow: R) {
    onRowChange(newRow);
  }

  function onRowSelectionChange(checked: boolean, isShiftClick: boolean, isCtrlClick: boolean) {
    onGridSelect({ rowIdx, columnIdx: -1, isShiftClick, isCtrlClick });
  }

  function checkIsSelf(event: React.MouseEvent) {
    return (event.nativeEvent.target as HTMLElement).closest('.ant-menu') === null;
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    const isShiftClick = event.nativeEvent.shiftKey;
    if (isShiftClick) {
      if (!isCellFirstSelected) {
        // selectCellWrapper(isShiftClick, false);
      }
      event.preventDefault();
      return;
    }
    if (!(event.target as HTMLElement)?.closest?.('.rdg-cell')) {
      return;
    }
    if (isSelectColumn || event.button !== 0) {
      return;
    }
    if (!checkIsSelf(event)) {
      return;
    }
    setMouseDown(true);
    setInnerMouseDownPosition({
      clientX: event.clientX,
      clientY: event.clientY,
      isSelected: isCellFirstSelected
    })
    if (column.clickable) {
      onGridSelect({ rowIdx, columnIdx, isShiftClick: false, isCtrlClick: false })
    }
    if (!isCellFirstSelected) {
      selectCellWrapper(isShiftClick, false);
    }
  }

  function handleMouseUp(event: React.MouseEvent<HTMLDivElement>) {
    const isShiftClick = event.nativeEvent.shiftKey;
    const { clientX, clientY, button } = event;
    if (button !== 0 || isSelectColumn || !innerMouseDownPosition) {
      return;
    }
    if (!checkIsSelf(event)) {
      return;
    }
    if (column.clickable) {
      if (innerMouseDownPosition && innerMouseDownPosition.clientX === clientX && innerMouseDownPosition.clientY === clientY) {
        /**
         * 确认是否也触发过mouseDown来确认是否为一个click事件
         */
        if (innerMouseDownPosition.isSelected) {
          /**
           * 是click，并且选中，打开editor
           */
          selectCellWrapper(isShiftClick, true)
        } else {
          selectCellWrapper(isShiftClick, column.editorOptions?.editOnClick);
        }
      }
    }
    onClick?.(event);
  }

  function handleMouseEnter() {
    if (lazyStore.get('isMouseDown')) {
      onGridSelect({ rowIdx, columnIdx, isShiftClick: false, isCtrlClick: false, isUpdateIndex: false});
    }
  }

  const baseCell = <div
    role="gridcell"
    aria-colindex={column.idx + 1} // aria-colindex is 1-based
    aria-rowindex={rowIdx}
    aria-selected={isCellSelected}
    ref={(enableRowRecord && isSelectColumn) ? unionRef : normalRef}
    title={toString(row[column.key as keyof R]) || ''}
    className={className}
    style={getCellStyle(column)}
    onDoubleClick={!isSelectColumn ? handleDoubleClick : undefined}
    onMouseDown={handleMouseDown}
    onMouseEnter={!isSelectColumn ? handleMouseEnter : undefined}
    onMouseUp={handleMouseUp}
    tabIndex={0}
    {...props}
  >
    {!column.rowGroup && (
      <>
        <column.formatter
          column={column}
          rowIdx={rowIdx}
          row={row}
          isCellSelected={isCellSelected}
          isRowSelected={isRowSelected}
          onRowSelectionChange={onRowSelectionChange}
          onRowChange={handleRowChange}
          onRowReorder={onRowReorder}
          enableRowRecord={enableRowRecord}
        />
        {dragHandleProps && (
          <div className={cellDragHandleClassname} {...dragHandleProps} />
        )}
      </>
    )}

  </div>
  return baseCell;
}

const MemoCell = memo(forwardRef(Cell), (prevProps, nextProps) => {
  if (prevProps.column?.key === SELECT_COLUMN_KEY && nextProps.isRowCellSelected !== prevProps.isRowCellSelected) {
    return false;
  }
  return isEqual(
    { ...prevProps, isMouseDown: false, selectedRange: '', isRowCellSelected: null },
    { ...nextProps, isMouseDown: false, selectedRange: '', isRowCellSelected: null }
  )
}) as <R, SR = unknown>(props: CellRendererProps<R, SR> & RefAttributes<HTMLDivElement>) => JSX.Element;
export default MemoCell;
// export default function <R, SR>(props: CellRendererProps<R, SR> & RefAttributes<HTMLDivElement>) {
//   const contextMenuStore = useContext(ContextMenuStore);
//   const { getContextMenuConfig, ContextMenuRender } = contextMenuStore;
//   const { setShowMenuRowIdx } = useContext(RowStore);
//   return <MemoCell
//     setShowMenuRowIdx={setShowMenuRowIdx}
//     getContextMenuConfig={getContextMenuConfig}
//     ContextMenuRender={ContextMenuRender}
//     {...props}
//   />
// }